import React, { useEffect, useState } from 'react'
import {
  XMarkIcon,
  CheckCircleIcon,
  BuildingOffice2Icon,
  IdentificationIcon,
  CurrencyDollarIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline'
import { Button } from '../../ui'
import {
  loadCmdmCreditDisplayInfo,
  type CmdmCreditDisplayInfo,
  type CmdmCreditDisplayOrder
} from '../../../utils/cmdmCreditDisplayUtils'

interface VerifyCmdmCreditModalProps {
  isOpen: boolean
  order: CmdmCreditDisplayOrder | null | undefined
  pedidoTotalCop?: number | null
  onClose: () => void
  onApprove: () => void | Promise<void>
  onReject: () => void | Promise<void>
  isLoading?: boolean
}

interface InfoRowProps {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  label: string
  value: string
}

function InfoRow({ icon: Icon, label, value }: InfoRowProps) {
  return (
    <div className="flex items-center gap-3 py-2.5">
      <Icon className="h-5 w-5 shrink-0 text-gray-500" aria-hidden />
      <div className="flex min-w-0 flex-1 flex-row items-center gap-x-2">
        <span className="shrink-0 text-sm font-semibold text-gray-800">{label}</span>
        <span className="min-w-0 text-sm text-gray-700">{value}</span>
      </div>
    </div>
  )
}

const VerifyCmdmCreditModal: React.FC<VerifyCmdmCreditModalProps> = ({
  isOpen,
  order,
  pedidoTotalCop,
  onClose,
  onApprove,
  onReject,
  isLoading = false
}) => {
  const [displayInfo, setDisplayInfo] = useState<CmdmCreditDisplayInfo | null>(null)

  useEffect(() => {
    if (!isOpen || !order) {
      setDisplayInfo(null)
      return
    }
    setDisplayInfo(loadCmdmCreditDisplayInfo(order, pedidoTotalCop))
  }, [isOpen, order, pedidoTotalCop])

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full"
      role="dialog"
      aria-modal="true"
      aria-labelledby="verify-cmdm-credit-title"
    >
      <div className="relative bg-white rounded-lg shadow-xl p-8 m-4 max-w-lg w-full">
        <div className="flex justify-between items-center mb-6">
          <h2 id="verify-cmdm-credit-title" className="text-2xl font-bold text-gray-800">
            Verificar Pago
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={isLoading}
            className="!p-1"
            aria-label="Cerrar"
          >
            <XMarkIcon className="h-6 w-6" />
          </Button>
        </div>

        <div className="space-y-1 mb-8">
          <InfoRow icon={BuildingOffice2Icon} label="Empresa:" value={displayInfo?.empresa ?? '—'} />
          <InfoRow icon={IdentificationIcon} label="NIT:" value={displayInfo?.nit ?? '—'} />
          <InfoRow
            icon={CurrencyDollarIcon}
            label="Crédito otorgado:"
            value={displayInfo?.creditoOtorgado ?? 'No disponible'}
          />
          <InfoRow
            icon={CalendarDaysIcon}
            label="Plazo del crédito:"
            value={displayInfo?.plazoCredito ?? '—'}
          />
        </div>

        <div className="flex justify-end gap-3">
          <Button
            variant="danger"
            onClick={() => void onReject()}
            disabled={isLoading}
            isLoading={isLoading}
            leftIcon={XMarkIcon}
          >
            Rechazar Crédito
          </Button>
          <Button
            variant="success"
            onClick={() => void onApprove()}
            disabled={isLoading}
            isLoading={isLoading}
            leftIcon={CheckCircleIcon}
          >
            Aprobar Crédito
          </Button>
        </div>
      </div>
    </div>
  )
}

export default VerifyCmdmCreditModal
